import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import styled from "styled-components";
import { FileRow } from "../../../common/components";
import { ClaimStatus, BesuClaimState, ClaimHistory, CommentRecord } from "../../../types/claim";
import { formatToDate, lastOfArray, shortenUUID } from "../../../utils/common";
import { ClaimApproversTable } from "./ClaimApproversTable";
import ClaimStatusDetail from "./ClaimStatusDetail";
import {
    PORejectType,
    PORejectedTable,
} from "../../invoice/components/PORejectedTable";
import {
    isNodeDeveloper, isUserContractor,
} from "../../../utils/role";
import { Contact } from "../../../types/common";
import { currentRolesSelector } from "../../../redux/slices/auth/selectors";
import { useDispatch } from "../../../redux/hooks";
import { getContactByAccountName } from "../../../redux/slices/accountInfo/thunks";
import { useSelector } from "react-redux";

type ClaimDetailProps = {
    className?: string;
    claim: BesuClaimState;
    claimHistory: ClaimHistory;
    hideStatus?: boolean;
    isDiscarded?: boolean;
};

const ClaimDetail = ({
    className,
    claim,
    claimHistory,
    hideStatus,
    isDiscarded,
}: ClaimDetailProps): JSX.Element => {
    const lastComment = claim.commentRecords[0];
    const currentRoles = useSelector(currentRolesSelector);
    const [currentContact, setCurrentContact] = useState<Contact>();

    // Use claim data first, then fallback to project data
    const developerAccountName = claim.developer?.accountName
    const contractorAccountName = claim.contractor?.accountName
    const dispatch = useDispatch();
    useEffect(() => {
        if (!isUserContractor(currentRoles)) {
            const getContactInfo = async () => {
                const contactResponse = await dispatch(getContactByAccountName(claim.contractor.accountName)).unwrap();
                const contact: Contact = {
                    company: contactResponse.name,
                    email: contactResponse.contactEmail,
                    contactPerson: contactResponse.contactPerson,
                    phone: contactResponse.contactNumber,
                };

                setCurrentContact(contact);
            };

            getContactInfo();
        }

        return () => {
            setCurrentContact(undefined);
        };
    }, [currentRoles, claim, dispatch]);
    return (
        <Card.Body className={`mb-5 border-0 ${className || ""} p-0`}>
            <Card.Header>
                <h2 className="mb-3 rem-1">
                    Claim Submission{" "}
                    <span className="fs-7 fw-bold">
                        #{shortenUUID(claim.referenceId)}
                    </span>
                    {!hideStatus && (
                        <ClaimStatusDetail
                            claimHistory={claimHistory}
                            latestState={claim}
                            noCA={claim.claimApprovers.length === 0}
                        />
                    )}
                </h2>
            </Card.Header>

            <Card.Body>
                <dl>
                {isNodeDeveloper() && currentContact && (
                    <>
                        <StyledDt isDiscarded={isDiscarded}>Service Provider</StyledDt>
                        <dd className="fw-bold mb-5">
                            {currentContact.company}
                        </dd>
                    </>
                )}
                <StyledDt isDiscarded={isDiscarded}>Creation Date</StyledDt>
                <dd className="fw-bold mb-5">{formatToDate(claim.createdAt)}</dd>

                <StyledDt isDiscarded={isDiscarded}>Claim Description</StyledDt>
                <dd className="text-pre-wrap fw-bold mb-5">
                    {claim.descriptionRef}
                </dd>

                <StyledDt isDiscarded={isDiscarded}>Document List</StyledDt>
                {claim.documents.map((file, index) => (
                    <dd key={index}>
                        <FileRow
                            file={{
                                id: file.documentId,
                                name: file.documentName,
                            }}
                            canDownload
                            downloadMeta={{
                                developerAccountName,
                                contractorAccountName,
                            }}
                        />
                    </dd>
                ))}
                <StyledDt className="mt-5" isDiscarded={isDiscarded}>
                    Claim Approvers
                </StyledDt>
                <dd>
                    <ClaimApproversTable
                        data={claim.claimApprovers}
                        comments={claim.commentRecords ?? []}
                    />
                </dd>
                {lastComment &&
                    !lastComment.resolved &&
                    claim.status === ClaimStatus.PO_REJECTED && (
                        <>
                            <StyledDt
                                className="mt-5"
                                isDiscarded={isDiscarded}
                            >
                                Project Officers
                            </StyledDt>
                            <dd>
                                <PORejectedTable
                                    comment={lastComment}
                                    rejectType={PORejectType.CLAIM_REJECT}
                                    status={claim.status}
                                />
                            </dd>
                        </>
                    )}
                </dl>
            </Card.Body>
        </Card.Body>
    );
};

const StyledDt = styled.dt<{ isDiscarded?: boolean }>`
    margin-bottom:0.5rem;
    color: ${(props) =>
        props.isDiscarded
            ? "var(--bs-placeholder-grey)"
            : "var(--bs-body-color)"};
`;

export default ClaimDetail;
